import { DescriptionOutlined, OpenInNew, Search as SearchIcon } from '@mui/icons-material'
import { Card, CardHeader, CardContent, Box, InputAdornment, TextField, Typography, Button } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { searchFieldSx } from '../../common/searchFieldSx'
import { blueSky, colors, spacingItem } from '../../theme'
import { DocumentItem } from './DocumentItem'
import { AddDocumentModal } from './AddDocumentModal'
import { CustomerDocumentDto } from '../../../interactors/gen/backendClient'
import { getReadableCustomerDocumentType } from '../../../domain/Customer/CustomerDisplay'
import { useMutation } from 'react-query'
import { CustomersClient } from '../../../interactors/clients/CustomerClient'
import { getCustomerDocumentsSrc } from '../../../utils/S3-links'
import { useAccountsStore } from '../../../store/AccountsStore'
import { addSecondaryFabSx } from '../../common/addPrimaryCtaSx'

interface DocumentsListProps {
  documents: CustomerDocumentDto[]
}

const petsoRessources = [
  'Certificat-Petso-Chien.pdf',
  'Certificat-Petso-Chat.pdf',
  'Certificat-Petso-Lapin.pdf',
  'Certificat-Petso-Furet.pdf',
]

export const DocumentsList: React.FC<DocumentsListProps> = ({ documents }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [documentsList, setDocumentsList] = useState<CustomerDocumentDto[]>(documents)

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }

  const filteredDocuments = documentsList
    ? documentsList.filter(
        (document) =>
          searchQuery === '' ||
          document.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          document.reference?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          getReadableCustomerDocumentType(document.type)?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : []

  const fetchCustomerMutation = useMutation(async () => await CustomersClient.getCurrentCustomer(), {
    onSuccess: (data) => {
      setDocumentsList(data.documents || [])
    },
  })

  useEffect(() => {
    fetchCustomerMutation.mutate()
  }, [])

  const accountsStore = useAccountsStore()

  const getPetsoResourceAnimal = (document: string) =>
    document.substring(document.lastIndexOf('-') + 1, document.indexOf('.', document.lastIndexOf('-')))

  return (
    <>
      <Box display="flex" flexDirection="row" flexWrap="nowrap" sx={{ ...spacingItem, backgroundColor: colors.white }}>
        <TextField
          id="name-input"
          placeholder="Rechercher un document"
          type="text"
          fullWidth
          value={searchQuery}
          onChange={handleSearch}
          sx={searchFieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Card>
        <CardHeader title="Documents" />
        <CardContent>
          <Typography variant="body1" component="p" sx={{ mt: -3, mb: 4 }}>
            Déposez ici les modèles génériques de certificat et de contrat utilisés par l’association, ils seront utile
            pour faciliter les démarches lors des procédures d’adoption
          </Typography>
          {filteredDocuments.length > 0 &&
            filteredDocuments.map((document) => (
              <DocumentItem key={document.id} document={document} setDocumentsList={setDocumentsList} />
            ))}
          <Box display="flex" justifyContent="flex-end" sx={spacingItem}>
            <AddDocumentModal fetchCustomerMutation={fetchCustomerMutation} disabled={!accountsStore.isAdmin()} />
          </Box>
        </CardContent>
        <CardHeader title="Ressources Petso" />
        <CardContent>
          {petsoRessources.map((document) => (
            <Box
              key={document}
              sx={{
                py: { xs: 1.5, sm: 1.75 },
                borderBottom: '1px solid rgba(122, 129, 151, 0.16)',
                '&:last-of-type': { borderBottom: 'none' },
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '32px 1fr', sm: '36px minmax(0, 1fr) auto' },
                  columnGap: { xs: 1, sm: 1.5, md: 2 },
                  rowGap: { xs: 1, sm: 0 },
                  alignItems: 'center',
                }}
              >
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    width: { xs: 32, sm: 36 },
                    height: { xs: 32, sm: 36 },
                    borderRadius: 2.5,
                    backgroundColor: 'rgba(105, 141, 172, 0.10)',
                    border: '1px solid rgba(105, 141, 172, 0.16)',
                  }}
                >
                  <DescriptionOutlined sx={{ width: { xs: 18, sm: 20 }, height: { xs: 18, sm: 20 }, color: blueSky }} />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: '#203864',
                      fontSize: { xs: 12.75, sm: 13.25, md: 13.5 },
                      fontWeight: 500,
                      lineHeight: 1.35,
                    }}
                  >
                    Modèle de certificat Petso pour {getPetsoResourceAnimal(document)}
                  </Typography>
                </Box>

                <Button
                  component="a"
                  href={getCustomerDocumentsSrc(document)}
                  target="_blank"
                  rel="noreferrer"
                  variant="outlined"
                  startIcon={<OpenInNew sx={{ fontSize: 18 }} />}
                  sx={{
                    ...addSecondaryFabSx,
                    justifySelf: { xs: 'start', sm: 'end' },
                    gridColumn: { xs: '2 / 3', sm: '3 / 4' },
                    px: 1.8,
                    minWidth: 'auto',
                  }}
                >
                  Consulter
                </Button>
              </Box>
            </Box>
          ))}
        </CardContent>
      </Card>
    </>
  )
}
